import fetch from 'isomorphic-fetch';

import metricsService from '../services/metrics';
import { rumService } from '../services/rum';
import { RUM_EVENT_STATUS } from '../constants/metrics';
import logger from './logger';
import { retry } from './promise';

type RequestOptions = {
  treat404AsOk?: boolean;
  treat400AsOk?: boolean;
  logWithRum?: boolean;
  logWithKatal?: boolean;
  metricNs: string;
};

/**
 * API wrapper which emits metrics and applies a retry strategy.
 * Uses either Katal metrics or RUM logging based on the logWithRum flag or both
 */
export const request = async (
  url: string,
  fetchOpts: RequestInit = {},
  options: RequestOptions
): Promise<Response> => {
  const {
    treat404AsOk = false,
    treat400AsOk = false,
    logWithRum = false,
    logWithKatal = true, // Default to true for backward compatibility
    metricNs,
  } = options;

  // Initialize logging based on flags
  const publisher = logWithKatal ? metricsService.getPublisher(metricNs) : null;
  const totalTimer = logWithKatal
    ? metricsService.createTimerStopWatch(`${metricNs}Time`).withMonitor()
    : null;
  const useKatal = logWithKatal && publisher;
  const startTime = logWithRum ? Date.now() : 0;

  return await retry(
    () => {
      const requestTimer = useKatal
        ? metricsService
            .createTimerStopWatch(`${metricNs}RequestTime`)
            .withMonitor()
        : null;
      const requestStartTime = logWithRum ? Date.now() : 0;

      return fetch(url, fetchOpts)
        .then((resp: Response) => {
          const is404Ok = resp.status === 404 && treat404AsOk;
          const is400Ok = resp.status === 400 && treat400AsOk;
          if (resp.ok || is404Ok || is400Ok) {
            // Log success
            if (logWithRum) {
              rumService.recordEvent(`${metricNs}${RUM_EVENT_STATUS.SUCCESS}`, {
                url,
                status: resp.status,
                duration: Date.now() - requestStartTime,
                totalDuration: Date.now() - startTime,
              });
            }
            if (useKatal) {
              publisher!.publishCounterMonitor(`${metricNs}Success`, 1);
            }
            return resp;
          }

          logger.error(`Endpoint [${url}] returns an error response: ${resp}`);

          if (useKatal) {
            publisher!.publishStringTruncate(
              `${metricNs}ErrorCode`,
              `${resp.status}`
            );
          }
          throw new Error(`${resp.status}: ${resp.statusText}`);
        })
        .catch((error: Error) => {
          logger.error('Encountered an expected error:', error);

          if (logWithRum) {
            rumService.recordEvent(
              `${metricNs}${RUM_EVENT_STATUS.REQUEST_ERROR}`,
              {
                url,
                error: error.message,
                duration: Date.now() - requestStartTime,
                userAgent: navigator.userAgent,
                path: `${window.location.pathname}${window.location.search}`,
              }
            );
          }

          if (useKatal) {
            publisher!.publishCounterMonitor(`${metricNs}RequestError`, 1);
            publisher!.publishStringTruncate(
              `${metricNs}ErrorMsg`,
              error.message || 'NA'
            );
            publisher!.publishStringTruncate(
              `${metricNs}ErrorUri`,
              `${window.location.pathname}${window.location.search}`
            );
            publisher!.publishStringTruncate(
              `${metricNs}UserAgent`,
              navigator.userAgent
            );
          }

          throw error;
        })
        .finally(() => {
          if (useKatal && requestTimer) {
            publisher!.publish(requestTimer);
          }
        });
    },
    {
      retries: 3,
      interval: 100,
    }
  )
    .catch((error: Error) => {
      // Captures when an error is impacting the customer experience, after
      // retries are exhausted.
      if (logWithRum) {
        rumService.recordEvent(`${metricNs}${RUM_EVENT_STATUS.FINAL_ERROR}`, {
          url,
          error: error.message,
          totalDuration: Date.now() - startTime,
        });
      }
      if (useKatal) {
        publisher!.publishCounterMonitor(`${metricNs}Error`, 1);
      }
      throw error;
    })
    .finally(() => {
      if (useKatal && totalTimer) {
        publisher!.publish(totalTimer);
      }
    });
};

export const requestWithFallbackUrl = async (
  url: string,
  fallbackUrl: string,
  fetchOpts: RequestInit = {},
  options: RequestOptions
): Promise<Response> => {
  try {
    return await request(url, fetchOpts, options);
  } catch (e) {
    logger.warn(
      `Request to URL '${url}' failed. Retrying with fallback URL '${fallbackUrl}'`,
      e
    );
    return await request(fallbackUrl, fetchOpts, {
      ...options,
      metricNs: `${options.metricNs}Fallback`,
    });
  }
};

// This function is helpful to join endpoint and API path
export function joinUrlPath(a: string, b: string) {
  return `${a.replace(/\/+$/, '')}/${b.replace(/^\/+/, '')}`;
}
